import { Typography } from '@material-ui/core';
import { Skeleton } from '@material-ui/lab';
import React from 'react';
import { ActivityMetricType } from 'shared/models/activity-metric';

import { buildLineChartOption } from 'shared/common/charts/options';

import { echarts, ReactEChartsCore } from '../../common/charts/echarts-core';
import { usePortalTooltip } from '../../common/charts/tooltip';
import {
    Field,
    FieldsSelected,
    getFieldColors,
    getFieldLabel,
    metricsByPeriod,
    metricsByTimeSeriesSpec,
    TimeSeriesSpec
} from '../../common/metrics';
import { useChartExport } from '../../hooks/use-chart-export';
import { ChartExportButtons } from '../charts/chart-export-buttons';
import { Legend } from './legend';

const defaultHeight = 480;
const defaultStrokeWidth = 3;
const defaultSmooth = 0.3;

export const MetricsTimeline: React.FC<{
    metrics?: Array<{ kind: ActivityMetricType; count: number; date: string }>;
    fields: ActivityMetricType[];
    duration: { start: number; end: number };
    selectedFields?: FieldsSelected;
    onChangeSelectedFields?: (updated: FieldsSelected) => void;
    title?: string;
    hideXAxisLabels?: boolean;
    height?: number;
    periodSize?: 'day' | 'week' | 'month';
    timeSeries?: TimeSeriesSpec;
    strokeWidth?: number;
    smooth?: number;
    chartOptions?: {
        grid?: Record<string, any>;
        yAxis?: Record<string, any>;
    };
}> = ({
    metrics,
    duration,
    fields: fieldOptions,
    title,
    selectedFields,
    onChangeSelectedFields,
    hideXAxisLabels,
    height,
    periodSize,
    timeSeries,
    strokeWidth,
    smooth = defaultSmooth,
    chartOptions: customChartOptions
}) => {
    const [hoveredField, setHoveredField] = React.useState<Field | undefined>(undefined);
    const chartExport = useChartExport({ title: title ?? 'metrics-timeline' });

    const safeMetrics = metrics ?? [];

    const timeSeriesData = timeSeries
        ? metricsByTimeSeriesSpec(safeMetrics, duration.start, duration.end, timeSeries)
        : undefined;
    const dataByPeriod = timeSeriesData
        ? timeSeriesData.series
        : metricsByPeriod(safeMetrics, duration.start, duration.end, periodSize);

    const periods = dataByPeriod?.map((d) => d.period) ?? [];
    const categories = periods.map((p) => p.tick);
    const fields = !selectedFields ? fieldOptions : fieldOptions.filter((f) => selectedFields[f]);
    const series = React.useMemo(
        () =>
            fields.map((f) => ({
                data: dataByPeriod.map((d) => d.metrics[f] ?? 0),
                name: getFieldLabel(f)
            })),
        [dataByPeriod, fields]
    );
    const colors = getFieldColors(fields, hoveredField);

    const tooltipFormatter = React.useCallback(
        (raw: any) => {
            const items = Array.isArray(raw) ? raw : [raw];
            if (!items.length) {
                return '';
            }
            const firstItem: any = items[0] ?? {};
            const index = firstItem.dataIndex ?? 0;
            const prefix = timeSeriesData?.labelPrefix ? `${timeSeriesData.labelPrefix}: ` : '';
            const periodLabel = `${prefix}${periods[index]?.label ?? firstItem.axisValue ?? ''}`;
            const rows = items
                .map((item) => {
                    if (!item) {
                        return '';
                    }
                    const normalized: any = item;
                    let value: number | string;
                    if (typeof normalized.data === 'number') {
                        value = normalized.data;
                    } else if (Array.isArray(normalized.data)) {
                        value = normalized.data[1];
                    } else if (Array.isArray(normalized.value)) {
                        value = normalized.value[1];
                    } else {
                        value = normalized.value;
                    }
                    return `
                        <div class="metrics-tooltip-row">
                            <span class="color-indicator" style="background:${normalized.color};"></span>
                            ${normalized.seriesName}: <strong>${value ?? 0}</strong>
                        </div>
                    `;
                })
                .join('');
            return `
                <div class="metrics-tooltip">
                    <div class="metrics-tooltip-header">${periodLabel}</div>
                    ${rows}
                </div>
            `;
        },
        [periods, timeSeriesData]
    );

    const tooltip = usePortalTooltip(
        {
            axisPointer: { type: 'line' },
            formatter: tooltipFormatter,
            trigger: 'axis'
        },
        [tooltipFormatter]
    );

    const chartOptions = React.useMemo(() => {
        if (!categories.length || !series.length) {
            return undefined;
        }

        return buildLineChartOption({
            categories,
            colors,
            grid: customChartOptions?.grid,
            hideXAxisLabels,
            series,
            smooth,
            strokeWidth: strokeWidth ?? defaultStrokeWidth,
            tooltip,
            yAxis: customChartOptions?.yAxis
        });
    }, [categories, colors, customChartOptions, hideXAxisLabels, series, smooth, strokeWidth, tooltip]);

    const isLoading = !metrics;

    const chart = isLoading ? (
        <Skeleton variant="rect" />
    ) : categories.length > 0 && chartOptions ? (
        <ReactEChartsCore
            ref={chartExport.chartRef}
            echarts={echarts}
            option={chartOptions}
            notMerge={true}
            lazyUpdate={true}
            style={{ height: height ?? defaultHeight }}
            opts={{ renderer: 'svg' }}
        />
    ) : null;

    const canExport = !isLoading && !!chartOptions && categories.length > 0;

    const header = title ? (
        <div className="panel-header">
            <div className="panel-title">
                <Typography variant="h5">{title}</Typography>
            </div>
            <div className="panel-options">
                <ChartExportButtons
                    disabled={!canExport}
                    isExporting={chartExport.isExporting}
                    onExportPng={chartExport.exportAsPng}
                    onExportSvg={chartExport.exportAsSvg}
                />
            </div>
        </div>
    ) : null;

    const legend =
        fieldOptions.length > 1 && selectedFields && onChangeSelectedFields ? (
            <Legend
                fields={fieldOptions}
                selected={selectedFields}
                onFieldHover={setHoveredField}
                onUpdateSelected={onChangeSelectedFields}
            />
        ) : null;

    return (
        <div className="chart-container">
            {header}
            <div className="panel-content">
                <div className="chart">{chart}</div>
                {legend}
            </div>
        </div>
    );
};
